/**
 * PersonaGuide — Bandeau de parcours guidé par persona dans le Lab
 * 
 * Affiche un workflow visuel adapté au persona sélectionné (Dev / QA / PO).
 * Chaque étape correspond à une section du Lab (LabNavigation sectionId).
 * L'utilisateur progresse en cliquant les étapes, avec suivi de progression.
 * 
 * Isolation : composant 100% standalone, contrôlé par feature flag.
 * Peut être retiré sans impact sur le Lab existant.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaCode, FaProjectDiagram, FaNetworkWired, FaShieldAlt,
  FaClipboardCheck, FaChartLine, FaFlask, FaBullseye,
  FaLightbulb, FaFileExport, FaUserTie, FaTimes,
  FaCheckCircle, FaArrowRight, FaRedo
} from 'react-icons/fa';
import './PersonaGuide.css';

// ─── Persona journey definitions ───────────────────────────────
// Each step maps to a LabNavigation sectionId
const PERSONA_JOURNEYS = {
  dev: {
    id: 'dev',
    icon: FaCode,
    color: '#667eea',
    steps: [
      { id: 'importCode', icon: FaCode, sectionId: 'importCode' },
      { id: 'quickCreate', icon: FaProjectDiagram, sectionId: 'quickCreate' },
      { id: 'nodesList', icon: FaNetworkWired, sectionId: 'nodesList' },
      { id: 'relations', icon: FaNetworkWired, sectionId: 'relations' },
      { id: 'analysis', icon: FaShieldAlt, sectionId: 'analysis' },
    ]
  },
  qa: {
    id: 'qa',
    icon: FaFlask,
    color: '#f5576c',
    steps: [
      { id: 'nodesList', icon: FaNetworkWired, sectionId: 'nodesList' },
      { id: 'tests', icon: FaClipboardCheck, sectionId: 'tests' },
      { id: 'analysis', icon: FaChartLine, sectionId: 'analysis' },
      { id: 'vectorSearch', icon: FaFlask, sectionId: 'vectorSearch' },
      { id: 'testCycles', icon: FaBullseye, sectionId: 'testCycles' },
    ]
  },
  po: {
    id: 'po',
    icon: FaUserTie,
    color: '#4facfe',
    steps: [
      { id: 'quickCreate', icon: FaLightbulb, sectionId: 'quickCreate' },
      { id: 'specifications', icon: FaLightbulb, sectionId: 'specifications' },
      { id: 'nodesList', icon: FaNetworkWired, sectionId: 'nodesList' },
      { id: 'analysis', icon: FaChartLine, sectionId: 'analysis' },
      { id: 'exportDocs', icon: FaFileExport, sectionId: 'nodesList' }, // Export is accessed through nodes list
    ]
  }
};

const PersonaGuide = ({ persona, activeSection, onOpenSection, onDismiss }) => {
  const { t } = useTranslation();
  const [completedSteps, setCompletedSteps] = useState(() => {
    try {
      const saved = localStorage.getItem(`pg_completed_${persona}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  const journey = PERSONA_JOURNEYS[persona];
  if (!journey) return null;

  const steps = journey.steps;
  const PersonaIcon = journey.icon;
  const progress = Math.round((completedSteps.length / steps.length) * 100);

  // ─── Persist completed steps ───────────────────────────────
  useEffect(() => {
    localStorage.setItem(`pg_completed_${persona}`, JSON.stringify(completedSteps));
  }, [completedSteps, persona]);

  // ─── Track which step is active based on activeSection from Lab ──
  useEffect(() => {
    if (activeSection) {
      const stepIndex = steps.findIndex(s => s.sectionId === activeSection);
      if (stepIndex >= 0) {
        setCurrentStepIndex(stepIndex);
        // Auto-mark as completed when user visits a section
        if (!completedSteps.includes(steps[stepIndex].id)) {
          setCompletedSteps(prev => [...prev, steps[stepIndex].id]);
        }
      }
    }
  }, [activeSection]);

  const handleStepClick = (step, index) => {
    setCurrentStepIndex(index);
    if (onOpenSection) {
      onOpenSection(step.sectionId);
    }
  };

  const handleReset = () => {
    setCompletedSteps([]);
    setCurrentStepIndex(0);
  };

  const handleNextStep = () => {
    const nextIncomplete = steps.findIndex((s, i) => i > currentStepIndex && !completedSteps.includes(s.id));
    if (nextIncomplete >= 0) {
      handleStepClick(steps[nextIncomplete], nextIncomplete);
    }
  };

  if (isMinimized) {
    return (
      <div className="pg-minimized" style={{ borderLeftColor: journey.color }}>
        <button className="pg-expand-btn" onClick={() => setIsMinimized(false)}>
          <PersonaIcon style={{ color: journey.color }} />
          <span>{t(`personaGuide.${persona}.name`)}</span>
          <span className="pg-mini-progress">{progress}%</span>
        </button>
      </div>
    );
  }

  return (
    <div className="pg-container" style={{ '--pg-color': journey.color }}>
      {/* ─── Header ─── */}
      <div className="pg-header">
        <div className="pg-header-left">
          <PersonaIcon className="pg-persona-icon" />
          <div className="pg-header-text">
            <h4>{t(`personaGuide.${persona}.title`)}</h4>
            <p>{t(`personaGuide.${persona}.subtitle`)}</p>
          </div>
        </div>
        <div className="pg-header-actions">
          <button className="pg-action-btn" onClick={handleReset} title={t('personaGuide.reset')}>
            <FaRedo />
          </button>
          <button className="pg-action-btn" onClick={() => setIsMinimized(true)} title={t('personaGuide.minimize')}>
            <FaTimes />
          </button>
          {onDismiss && (
            <button className="pg-action-btn pg-dismiss" onClick={onDismiss} title={t('personaGuide.dismiss')}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ─── Progress bar ─── */}
      <div className="pg-progress-bar">
        <div className="pg-progress-fill" style={{ width: `${progress}%` }} />
        <span className="pg-progress-label">
          {completedSteps.length}/{steps.length} {t('personaGuide.stepsCompleted')}
        </span>
      </div>

      {/* ─── Steps ─── */}
      <div className="pg-steps">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isCompleted = completedSteps.includes(step.id);
          const isActive = currentStepIndex === index;
          const isCurrent = activeSection === step.sectionId;

          return (
            <React.Fragment key={step.id}>
              <button
                className={`pg-step ${isCompleted ? 'pg-step-completed' : ''} ${isActive ? 'pg-step-active' : ''} ${isCurrent ? 'pg-step-current' : ''}`}
                onClick={() => handleStepClick(step, index)}
              >
                <div className="pg-step-indicator">
                  {isCompleted ? (
                    <FaCheckCircle className="pg-step-check" />
                  ) : (
                    <span className="pg-step-number">{index + 1}</span>
                  )}
                </div>
                <StepIcon className="pg-step-icon" />
                <span className="pg-step-label">{t(`personaGuide.steps.${step.id}`)}</span>
              </button>
              {index < steps.length - 1 && (
                <FaArrowRight className={`pg-step-arrow ${isCompleted ? 'pg-arrow-completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ─── Next action CTA ─── */}
      {completedSteps.length < steps.length && (
        <div className="pg-next-action">
          <button className="pg-next-btn" onClick={handleNextStep}>
            {t('personaGuide.nextStep')} <FaArrowRight />
          </button>
        </div>
      )}

      {/* ─── Completion message ─── */}
      {completedSteps.length === steps.length && (
        <div className="pg-complete">
          <span>🎉 {t('personaGuide.allComplete')}</span>
        </div>
      )}
    </div>
  );
};

export default PersonaGuide;
